// src/services/clientServiceNew.js
import { 
  createService, 
  createFileUploadConfig, 
  createNestedCollection
} from './factory';
import { objectToQueryString } from '../utils/apiUtils';

// Create the base client service using our factory
const clientServiceBase = createService('Client', '/clients', {
  // File upload configuration for client logo
  fileUploadOptions: createFileUploadConfig('logo', 'logo'),
  
  // Nested collections (one-to-many relationships)
  nestedCollections: [
    createNestedCollection('brands', 'brand'),
    createNestedCollection('team', 'teamMember'),
    createNestedCollection('invoices', 'invoice'),
    createNestedCollection('campaigns', 'campaign'),
    createNestedCollection('content', 'content'),
    createNestedCollection('users', 'user')
  ]
});

// ========================
// Public API (backward compatible with existing clientService)
// This ensures all existing code continues to work with the new implementation
// ========================

// Get clients list with pagination and filters
export const getClients = async (params = {}) => {
  return clientServiceBase.getAll(params);
};

// Get single client by ID
export const getClient = async (clientId) => {
  return clientServiceBase.getById(clientId);
};

// Create a new client
export const createClient = async (clientData) => {
  return clientServiceBase.create(clientData);
};

// Update an existing client
export const updateClient = async (clientId, clientData) => {
  return clientServiceBase.update(clientId, clientData);
};

// Delete a client
export const deleteClient = async (clientId) => {
  return clientServiceBase.delete(clientId);
};

// Upload client logo
export const uploadClientLogo = async (clientId, logoFile) => {
  return clientServiceBase.uploadFile(clientId, logoFile);
};

// Get client brands
export const getClientBrands = async (clientId) => {
  return clientServiceBase.getBrands(clientId);
};

// Get client users
export const getClientUsers = async (clientId) => {
  return clientServiceBase.getUsers(clientId);
};

// Remove user from client
export const removeUserFromClient = async (clientId, userId) => {
  return clientServiceBase.removeTeamMember(clientId, userId);
};

// Assign users to client
export const assignUsersToClient = async (clientId, userIds) => {
  return clientServiceBase.addTeamMember(clientId, { userIds });
};

// Get client content
export const getClientContent = async (clientId, params = {}) => {
  return clientServiceBase.getContent(clientId, params);
};

// Get client campaigns
export const getClientCampaigns = async (clientId, params = {}) => {
  return clientServiceBase.getCampaigns(clientId, params);
};

// Get client invoices
export const getClientInvoices = async (clientId, params = {}) => {
  return clientServiceBase.getInvoices(clientId, params);
};

// Get client analytics
export const getClientAnalytics = async (clientId, params = {}) => {
  const queryString = objectToQueryString(params);
  // Import api directly since it's not exposed through the service
  const { api } = require('./api');
  return api.get(`/clients/${clientId}/analytics${queryString}`);
};

// Export the base service for direct usage if needed
export const clientService = clientServiceBase;